/**
 * Video Exporter
 * 
 * Exports video with overlays using Canvas and MediaRecorder.
 * Supports multiple formats for better compatibility.
 *
 * The recording loop is deliberately driven by a worker timer and manual
 * `requestFrame()` calls rather than requestAnimationFrame + automatic canvas
 * capture, so an export keeps running (and stays in sync) while the tab is in
 * the background. See the comment on `exportVideo` for the full rationale.
 */

import type { OverlayElement, ShapeOverlay, TextOverlay, BlurOverlay, ImageOverlay, RGBA } from '@/types';
import { createBackgroundInterval, scheduleBackgroundTimeout, acquireWakeLock } from '@/utils/backgroundTicker';
import { drawBlurredRegion } from '@/utils/blurEffect';
import { calculateAnimationTransform } from '@/utils/animationUtils';
import type { StopTicker } from '@/utils/backgroundTicker';

// Supported export formats with their MIME types and extensions
export const EXPORT_FORMATS = {
  'mp4-h264': {
    mimeType: 'video/mp4; codecs=avc1.42E01E',
    fallbackMimeType: 'video/mp4',
    extension: '.mp4',
    label: 'MP4 (H.264) - Best Compatibility',
    description: 'Works on all devices including macOS, iOS, Windows',
  },
  'webm-vp9': {
    mimeType: 'video/webm; codecs=vp9',
    fallbackMimeType: 'video/webm; codecs=vp8',
    extension: '.webm',
    label: 'WebM (VP9) - Smaller Size',
    description: 'Best for web, Chrome, Firefox',
  },
  'social-mp4': {
    mimeType: 'video/mp4; codecs=avc1.42E01E',
    fallbackMimeType: 'video/mp4',
    extension: '.mp4',
    label: 'Social Media (Instagram/TikTok)',
    description: 'Best compatibility for mobile apps',
  },
  'webm-vp8': {
    mimeType: 'video/webm; codecs=vp8',
    fallbackMimeType: 'video/webm',
    extension: '.webm',
    label: 'WebM (VP8) - Wide Support',
    description: 'Good for web browsers',
  },
} as const;

export type ExportFormat = keyof typeof EXPORT_FORMATS;

// Check which formats are supported by the browser
export const getSupportedFormats = (): ExportFormat[] => {
  const supported: ExportFormat[] = [];
  
  for (const [format, config] of Object.entries(EXPORT_FORMATS)) {
    if (MediaRecorder.isTypeSupported(config.mimeType) || 
        MediaRecorder.isTypeSupported(config.fallbackMimeType)) {
      supported.push(format as ExportFormat);
    }
  }
  
  return supported;
};

// Get the best available MIME type for a format
const getBestMimeType = (format: ExportFormat): string => {
  const config = EXPORT_FORMATS[format];
  if (MediaRecorder.isTypeSupported(config.mimeType)) {
    return config.mimeType;
  }
  if (MediaRecorder.isTypeSupported(config.fallbackMimeType)) {
    return config.fallbackMimeType;
  }
  // Ultimate fallback
  return 'video/webm';
};

// Convert RGBA to CSS string
const rgbaToString = (color: RGBA): string => {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
};

// Helper to draw tiles
const drawTiledOverlay = (
  ctx: CanvasRenderingContext2D,
  overlay: TextOverlay | ImageOverlay,
  drawSingle: (ctx: CanvasRenderingContext2D, x: number, y: number) => void
) => {
  const { tile, rotation } = overlay;
  if (!tile?.enabled) {
    // Draw single instance if tiling is disabled
    const { x, y, width, height } = overlay.bounds;
    
    ctx.save();
    
    if (rotation !== 0) {
      const centerX = x + width / 2;
      const centerY = y + height / 2;
      ctx.translate(centerX, centerY);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-centerX, -centerY);
    }
    
    // Apply opacity here for non-tiled. Multiplied, not assigned, so the
    // animation alpha set by the caller survives.
    ctx.globalAlpha *= overlay.style.opacity / 100;
    
    drawSingle(ctx, x, y);
    ctx.restore();
    return;
  }

  // TILING LOGIC (Duplicate of CanvasRenderer logic but without scale factor for export)
  const { gap, angle, opacity } = tile;
  const canvasWidth = ctx.canvas.width;
  const canvasHeight = ctx.canvas.height;
  
  // Use tile opacity or fallback to style opacity
  const tileOpacity = opacity !== undefined ? opacity : overlay.style.opacity;
  
  ctx.save();
  ctx.globalAlpha *= tileOpacity / 100;
  
  // Rotate the entire canvas pattern if angle is set
  if (angle !== 0) {
    const cx = canvasWidth / 2;
    const cy = canvasHeight / 2;
    ctx.translate(cx, cy);
    ctx.rotate((angle * Math.PI) / 180);
    ctx.translate(-cx, -cy);
  }
  
  const width = overlay.bounds.width;
  const height = overlay.bounds.height;
  const stepX = width + gap; // No scale factor here as we are drawing 1:1 on export canvas
  const stepY = height + gap;
  
  // Calculate coverage area (overshoot to cover rotation)
  const diag = Math.sqrt(canvasWidth * canvasWidth + canvasHeight * canvasHeight);
  const startX = -diag;
  const startY = -diag;
  const endX = diag * 2;
  const endY = diag * 2;
  
  for (let y = startY; y < endY; y += stepY) {
    // Offset every other row for brick pattern look
    const rowOffset = (Math.floor((y - startY) / stepY) % 2) * (stepX / 2);
    for (let x = startX; x < endX; x += stepX) {
      drawSingle(ctx, x + rowOffset, y);
    }
  }
  
  ctx.restore();
};

// Draw overlay on canvas
const drawOverlay = (
  ctx: CanvasRenderingContext2D,
  overlay: OverlayElement,
  imageMap: Map<string, HTMLImageElement>
) => {
  const { bounds, style, rotation } = overlay;
  
  // For non-tiled overlays, common setup
  if (overlay.type !== 'text' && overlay.type !== 'image') {
    ctx.save();
    ctx.globalAlpha *= style.opacity / 100;
    
    // Apply rotation
    if (rotation !== 0) {
      const centerX = bounds.x + bounds.width / 2;
      const centerY = bounds.y + bounds.height / 2;
      ctx.translate(centerX, centerY);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-centerX, -centerY);
    }
  }
  
  switch (overlay.type) {
    case 'rectangle':
    case 'rounded-rectangle': {
      const shape = overlay as ShapeOverlay;
      ctx.beginPath();
      
      if (overlay.type === 'rounded-rectangle') {
        const r = Math.min(shape.cornerRadius ?? 12, bounds.width / 2, bounds.height / 2);
        ctx.moveTo(bounds.x + r, bounds.y);
        ctx.lineTo(bounds.x + bounds.width - r, bounds.y);
        ctx.quadraticCurveTo(bounds.x + bounds.width, bounds.y, bounds.x + bounds.width, bounds.y + r);
        ctx.lineTo(bounds.x + bounds.width, bounds.y + bounds.height - r);
        ctx.quadraticCurveTo(bounds.x + bounds.width, bounds.y + bounds.height, bounds.x + bounds.width - r, bounds.y + bounds.height);
        ctx.lineTo(bounds.x + r, bounds.y + bounds.height);
        ctx.quadraticCurveTo(bounds.x, bounds.y + bounds.height, bounds.x, bounds.y + bounds.height - r);
        ctx.lineTo(bounds.x, bounds.y + r);
        ctx.quadraticCurveTo(bounds.x, bounds.y, bounds.x + r, bounds.y);
      } else {
        ctx.rect(bounds.x, bounds.y, bounds.width, bounds.height);
      }
      
      ctx.closePath();
      ctx.fillStyle = rgbaToString(style.fillColor);
      ctx.fill();
      
      if (style.borderWidth > 0) {
        ctx.strokeStyle = rgbaToString(style.borderColor);
        ctx.lineWidth = style.borderWidth;
        ctx.stroke();
      }
      ctx.restore(); // Close the block opened at top
      break;
    }
    
    case 'circle':
    case 'ellipse': {
      ctx.beginPath();
      const centerX = bounds.x + bounds.width / 2;
      const centerY = bounds.y + bounds.height / 2;
      ctx.ellipse(centerX, centerY, bounds.width / 2, bounds.height / 2, 0, 0, Math.PI * 2);
      ctx.fillStyle = rgbaToString(style.fillColor);
      ctx.fill();
      
      if (style.borderWidth > 0) {
        ctx.strokeStyle = rgbaToString(style.borderColor);
        ctx.lineWidth = style.borderWidth;
        ctx.stroke();
      }
      ctx.restore();
      break;
    }
    
    case 'polygon': {
      const polygonOverlay = overlay as any;
      if (polygonOverlay.polygon?.points?.length > 0) {
        ctx.beginPath();
        const points = polygonOverlay.polygon.points;
        ctx.moveTo(bounds.x + points[0].x, bounds.y + points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(bounds.x + points[i].x, bounds.y + points[i].y);
        }
        ctx.closePath();
        ctx.fillStyle = rgbaToString(style.fillColor);
        ctx.fill();
        
        if (style.borderWidth > 0) {
          ctx.strokeStyle = rgbaToString(style.borderColor);
          ctx.lineWidth = style.borderWidth;
          ctx.stroke();
        }
      }
      ctx.restore();
      break;
    }
    
    case 'text': {
      const textOverlay = overlay as TextOverlay;
      const { text } = textOverlay;
      
      drawTiledOverlay(ctx, textOverlay, (context, x, y) => {
        if (text.hasBackground && text.backgroundColor) {
          context.fillStyle = rgbaToString(text.backgroundColor);
          context.fillRect(x, y, bounds.width, bounds.height);
        }
        
        const fontStyle = text.fontStyle === 'italic' ? 'italic ' : '';
        const fontWeight = text.fontWeight === 'bold' ? 'bold ' : '';
        context.font = `${fontStyle}${fontWeight}${text.fontSize}px ${text.fontFamily}`;
        context.fillStyle = rgbaToString(style.fillColor);
        context.textAlign = text.textAlign;
        context.textBaseline = 'middle';
        
        let textX = x;
        if (text.textAlign === 'center') textX = x + bounds.width / 2;
        else if (text.textAlign === 'right') textX = x + bounds.width;

        // Same line handling as the preview renderer — a single fillText call
        // rendered "\n" as a blank glyph and squashed the whole caption onto
        // one line.
        const lines = String(text.content ?? '').split(/\r?\n/);
        const lineHeight = text.fontSize * 1.2;
        const blockTop = y + bounds.height / 2 - ((lines.length - 1) * lineHeight) / 2;
        lines.forEach((line, i) => {
          context.fillText(line, textX, blockTop + i * lineHeight);
        });
      });
      break;
    }
    
    case 'image': {
      const img = imageMap.get(overlay.id);
      const imageOverlay = overlay as ImageOverlay;
      if (img) {
        drawTiledOverlay(ctx, imageOverlay, (context, x, y) => {
          if (!imageOverlay.image.preserveAspectRatio || !img.naturalWidth) {
            context.drawImage(img, x, y, bounds.width, bounds.height);
            return;
          }
          // Letterbox inside the box rather than stretching — matches the
          // preview, which now honours the same flag.
          const ratio = Math.min(bounds.width / img.naturalWidth, bounds.height / img.naturalHeight);
          const drawW = img.naturalWidth * ratio;
          const drawH = img.naturalHeight * ratio;
          context.drawImage(
            img,
            x + (bounds.width - drawW) / 2,
            y + (bounds.height - drawH) / 2,
            drawW,
            drawH
          );
        });
      }
      break;
    }
    
    case 'blur': {
      const blurOverlay = overlay as BlurOverlay;
      const { blur } = blurOverlay;

      // Sample the OUTPUT CANVAS, not the video element.
      //
      // ExportPanel pre-scales overlay bounds to the export resolution, but
      // the old code fed those output-space bounds to drawImage as a *source*
      // rect on the video, whose pixel space is the source resolution. At the
      // default 1080p from a 720p source every region was sampled 1.5x too
      // large and offset, and any region near the right or bottom edge fell
      // entirely outside the frame — drawImage then copied nothing, so the
      // "blurred" patch was transparent and the export came out completely
      // unblurred no matter how high the intensity went.
      //
      // The frame is drawn to the canvas at output scale before any overlay,
      // so in canvas space the sample rect and the destination rect are the
      // same rect, and lower-z overlays get blurred along with the video.
      const painted = drawBlurredRegion(
        ctx,
        ctx.canvas,
        { x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height },
        { x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height },
        { blurType: blur.blurType, intensity: blur.intensity },
        { width: ctx.canvas.width, height: ctx.canvas.height }
      );

      if (!painted) {
        ctx.fillStyle = 'rgba(128, 128, 128, 0.6)';
        ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
      }

      if (style.borderWidth > 0) {
        ctx.strokeStyle = rgbaToString(style.borderColor);
        ctx.lineWidth = style.borderWidth;
        ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
      }

      ctx.restore();
      break;
    }
  }
};

// Preload images
const preloadImages = async (overlays: OverlayElement[]): Promise<Map<string, HTMLImageElement>> => {
  const imageMap = new Map<string, HTMLImageElement>();
  const imageOverlays = overlays.filter((o) => o.type === 'image') as ImageOverlay[];
  
  await Promise.all(
    imageOverlays.map((overlay) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          imageMap.set(overlay.id, img);
          resolve();
        };
        img.onerror = () => resolve();
        img.src = overlay.image.src;
      });
    })
  );
  
  return imageMap;
};

export interface TrimRange {
  startTime: number;
  endTime: number;
  enabled: boolean;
}

export interface ExportOptions {
  video: HTMLVideoElement;
  videoBlob: Blob;
  overlays: OverlayElement[];
  width: number;
  height: number;
  quality: 'low' | 'medium' | 'high';
  format: ExportFormat;
  trimRange?: TrimRange | null;
  onProgress?: (progress: number, phase: string) => void;
}

export interface ExportResult {
  blob: Blob;
  url: string;
  format: ExportFormat;
  extension: string;
}

// Global export state for cancellation
let currentExportController: AbortController | null = null;
let currentExportCleanup: (() => void) | null = null;

/**
 * Cancel any ongoing export
 */
export const cancelExport = (): void => {
  if (currentExportController) {
    currentExportController.abort();
    currentExportController = null;
  }
  if (currentExportCleanup) {
    currentExportCleanup();
    currentExportCleanup = null;
  }
};

/**
 * Export video with overlays.
 *
 * Supports trimming — only exports the portion between trimRange.startTime and
 * trimRange.endTime. Uses its own hidden video element so the UI player is
 * untouched.
 *
 * ── Why this loop looks the way it does ──────────────────────────────────────
 *
 * Three bugs used to make exports freeze mid-picture while the voice kept
 * going, and drift further out of sync the longer the video ran:
 *
 *  1. The render loop was driven by `requestAnimationFrame`. rAF is suspended
 *     outright in a hidden/background tab, so the moment the user switched
 *     away nothing was drawn any more — while the <video> element kept playing
 *     and the audio graph kept feeding the recorder. Result: the picture froze
 *     on its last frame and the audio ran on without it. The loop now ticks
 *     from a worker timer (see backgroundTicker.ts), which browsers do not
 *     throttle.
 *
 *  2. `canvas.captureStream(30)` samples the canvas off the compositor, so
 *     even a canvas we *did* draw to produces no frames while the page isn't
 *     being composited. We now use `captureStream(0)` and push each frame
 *     explicitly with `requestFrame()`, which is independent of compositing.
 *
 *  3. Picture and sound came from two *separate* <video> elements playing in
 *     parallel. Two media elements have two independent clocks: they start
 *     together and slowly diverge, and any decode hiccup on one of them shifts
 *     it permanently against the other — which is why the misalignment was
 *     always worst at the end. There is now a single element; its audio is
 *     tapped through a MediaElementSource, so sound and picture cannot drift
 *     apart by construction.
 *
 * Also fixed: the AudioContext was never resumed. When it was created in the
 * suspended state the recorder captured silence (or started late), offsetting
 * the whole audio track against the picture.
 */
export const exportVideo = async (options: ExportOptions): Promise<ExportResult> => {
  const { videoBlob, overlays, width, height, quality, format, trimRange, onProgress } = options;

  // Cancel any existing export
  cancelExport();

  // Create new abort controller
  const abortController = new AbortController();
  currentExportController = abortController;

  // Calculate effective trim bounds - check if trimRange exists and is enabled
  const isTrimming = trimRange !== null && trimRange !== undefined && trimRange.enabled === true;
  const trimStart = isTrimming ? trimRange.startTime : 0;

  // Frames per second we push into the recorder. Matching the previous
  // capture rate keeps output size predictable, and drawing 30×/s instead of
  // at display refresh (typically 60–120 Hz) roughly halves the per-frame CPU
  // cost — which is what stops slower machines from dropping frames.
  const TARGET_FPS = 30;
  const FRAME_INTERVAL_MS = 1000 / TARGET_FPS;

  return new Promise((resolve, reject) => {
    // Track resources for cleanup
    let exportVideoEl: HTMLVideoElement | null = null;
    let recorder: MediaRecorder | null = null;
    let audioContext: AudioContext | null = null;
    let videoUrl: string | null = null;
    let stopTicker: StopTicker | null = null;
    let releaseWakeLock: (() => void) | null = null;
    let onAbort: (() => void) | null = null;

    // Cleanup function
    const cleanup = () => {
      if (stopTicker) {
        stopTicker();
        stopTicker = null;
      }
      if (releaseWakeLock) {
        releaseWakeLock();
        releaseWakeLock = null;
      }
      if (onAbort) {
        abortController.signal.removeEventListener('abort', onAbort);
        onAbort = null;
      }
      if (exportVideoEl) {
        exportVideoEl.pause();
        // Drop the handlers before load(): clearing the src fires an error
        // event, and the loadedmetadata rejector must not see it.
        exportVideoEl.onloadedmetadata = null;
        exportVideoEl.onerror = null;
        exportVideoEl.removeAttribute('src');
        exportVideoEl.load();
        exportVideoEl = null;
      }
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop(); } catch { /* ignore */ }
      }
      if (audioContext) {
        try { void audioContext.close(); } catch { /* ignore */ }
        audioContext = null;
      }
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
        videoUrl = null;
      }
      currentExportCleanup = null;
    };

    currentExportCleanup = cleanup;

    const fail = (error: Error) => {
      cleanup();
      reject(error);
    };

    // Check for abort
    if (abortController.signal.aborted) {
      fail(new Error('Export cancelled'));
      return;
    }

    void (async () => {
      try {
        onProgress?.(0, 'preparing');

        // Preload images
        const imageMap = await preloadImages(overlays);

        // Create output canvas. `desynchronized` lets the browser skip the
        // usual display-sync round trip for our draws — we are capturing the
        // canvas, not showing it.
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });

        if (!ctx) {
          throw new Error('Failed to create canvas context');
        }

        // ── One hidden video element for BOTH picture and sound ─────────────
        // Two elements meant two clocks, and two clocks always drift. See the
        // header comment.
        exportVideoEl = document.createElement('video');
        exportVideoEl.playsInline = true;
        exportVideoEl.preload = 'auto';
        videoUrl = URL.createObjectURL(videoBlob);
        exportVideoEl.src = videoUrl;

        // Wait for video to load
        await new Promise<void>((res, rej) => {
          if (!exportVideoEl) return rej(new Error('Video element not created'));
          exportVideoEl.onloadedmetadata = () => res();
          exportVideoEl.onerror = () => rej(new Error('Failed to load video for export'));
        });

        if (abortController.signal.aborted) {
          fail(new Error('Export cancelled'));
          return;
        }

        const sourceDuration = exportVideoEl.duration;
        // Fall back to the `ended` event when the container reports no usable
        // duration (streamed/fragmented blobs sometimes report Infinity).
        const rawEnd = isTrimming ? trimRange!.endTime : sourceDuration;
        const trimEnd =
          isFinite(rawEnd) && rawEnd > trimStart ? rawEnd : Number.POSITIVE_INFINITY;
        const trimmedDuration = isFinite(trimEnd) ? trimEnd - trimStart : 0;

        console.log('Export will render:', {
          isTrimming,
          trimStart,
          trimEnd,
          trimmedDuration,
          videoDuration: sourceDuration,
        });

        // Determine bitrate based on quality and resolution
        const pixelCount = width * height;
        const baseBitrate = pixelCount * 0.15;
        const qualityMultiplier = quality === 'high' ? 4 : quality === 'medium' ? 2.5 : 1.5;
        const videoBitrate = Math.round(baseBitrate * qualityMultiplier);

        // Get the best MIME type for the selected format
        const mimeType = getBestMimeType(format);
        const formatConfig = EXPORT_FORMATS[format];

        console.log(`Exporting with format: ${format}, mimeType: ${mimeType}`);

        // ── Canvas → video track ─────────────────────────────────────────────
        // captureStream(0) hands us a track that only emits the frames we push
        // with requestFrame(), instead of sampling whatever the compositor
        // happens to paint. That is what keeps frames flowing while the tab is
        // in the background. Older engines without requestFrame() fall back to
        // automatic sampling.
        let canvasStream = canvas.captureStream(0);
        let videoTrack = canvasStream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack | undefined;
        const manualFrames = typeof videoTrack?.requestFrame === 'function';

        if (!manualFrames) {
          canvasStream.getTracks().forEach((t) => t.stop());
          canvasStream = canvas.captureStream(TARGET_FPS);
          videoTrack = canvasStream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack;
          console.warn('captureStream(0)/requestFrame unavailable — falling back to timed capture');
        }

        // ── Audio tapped off the SAME element ────────────────────────────────
        // MediaElementSource re-routes the element's output into the graph, so
        // connecting it only to the stream destination (never to
        // audioContext.destination) means nothing is audible locally.
        let audioTrack: MediaStreamTrack | null = null;
        exportVideoEl.muted = false;
        exportVideoEl.volume = 1;

        try {
          const AudioCtor: typeof AudioContext =
            window.AudioContext ?? (window as any).webkitAudioContext;
          audioContext = new AudioCtor();
          const source = audioContext.createMediaElementSource(exportVideoEl);
          const destination = audioContext.createMediaStreamDestination();
          source.connect(destination);

          // A context created in the 'suspended' state produces silence until
          // it is resumed — this was silently offsetting the whole audio track.
          if (audioContext.state === 'suspended') {
            await audioContext.resume();
          }
          if (audioContext.state !== 'running') {
            console.warn(`AudioContext is ${audioContext.state} — audio may be missing`);
          }

          audioTrack = destination.stream.getAudioTracks()[0] ?? null;
        } catch (e) {
          console.warn('Could not capture audio:', e);
        }

        if (!audioTrack) {
          // No graph to swallow the element's output — mute it so the export
          // doesn't play out loud through the speakers.
          exportVideoEl.muted = true;
        }

        // Combine video and audio streams
        const combinedStream = new MediaStream();
        canvasStream.getVideoTracks().forEach((track) => combinedStream.addTrack(track));
        if (audioTrack) {
          combinedStream.addTrack(audioTrack);
        }

        // Setup MediaRecorder
        recorder = new MediaRecorder(combinedStream, {
          mimeType,
          videoBitsPerSecond: videoBitrate,
        });

        const chunks: Blob[] = [];
        let exportComplete = false;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        recorder.onstop = () => {
          if (exportComplete) return; // Prevent double resolve
          exportComplete = true;

          cleanup();

          const outputMimeType = mimeType.split(';')[0];
          const blob = new Blob(chunks, { type: outputMimeType });
          const url = URL.createObjectURL(blob);

          onProgress?.(100, 'complete');
          resolve({
            blob,
            url,
            format,
            extension: formatConfig.extension,
          });
        };

        recorder.onerror = (e: any) => {
          if (exportComplete) return;
          exportComplete = true;
          fail(new Error(`Recording error: ${e.error?.name || 'Unknown'}`));
        };

        // Listen for abort
        onAbort = () => {
          if (exportComplete) return;
          exportComplete = true;
          fail(new Error('Export cancelled'));
        };
        abortController.signal.addEventListener('abort', onAbort);

        onProgress?.(5, 'preparing');

        // ── Seek to the trim start before recording anything ─────────────────
        const seekTo = (element: HTMLVideoElement, time: number) =>
          new Promise<void>((res) => {
            if (Math.abs(element.currentTime - time) < 0.01) {
              res();
              return;
            }
            const onSeeked = () => {
              element.removeEventListener('seeked', onSeeked);
              res();
            };
            element.addEventListener('seeked', onSeeked);
            element.currentTime = time;
          });

        await seekTo(exportVideoEl, trimStart);

        if (abortController.signal.aborted) {
          fail(new Error('Export cancelled'));
          return;
        }

        // Draw the composed frame for `time` and push it to the recorder.
        const videoEl = exportVideoEl;
        const drawComposedFrame = (time: number) => {
          ctx.drawImage(videoEl, 0, 0, width, height);

          const visibleOverlays = overlays
            .filter((o) => o.visible && time >= o.startTime && time <= o.endTime)
            .sort((a, b) => a.zIndex - b.zIndex);

          for (const overlay of visibleOverlays) {
            // Overlay animations (fade / slide / scale) are part of what the
            // preview shows, so the export has to run them too — it used to
            // draw every overlay in its static end state, which meant every
            // animation the user set up was silently dropped from the file.
            const anim = calculateAnimationTransform(
              overlay.animation,
              time,
              overlay.startTime,
              overlay.endTime,
              overlay.bounds
            );
            if (anim.opacity <= 0) continue;

            ctx.save();
            ctx.globalAlpha = anim.opacity;

            if (anim.translateX !== 0 || anim.translateY !== 0 || anim.scale !== 1) {
              const centerX = overlay.bounds.x + overlay.bounds.width / 2;
              const centerY = overlay.bounds.y + overlay.bounds.height / 2;
              ctx.translate(centerX, centerY);
              ctx.translate(anim.translateX, anim.translateY);
              ctx.scale(anim.scale, anim.scale);
              ctx.translate(-centerX, -centerY);
            }

            drawOverlay(ctx, overlay, imageMap);
            ctx.restore();
          }

          if (manualFrames) {
            videoTrack?.requestFrame();
          }
        };

        // Seed the first frame so the recording never opens on a blank canvas.
        drawComposedFrame(trimStart);

        console.log('Starting recording from:', videoEl.currentTime);
        recorder.start(1000);

        onProgress?.(10, 'rendering');

        // Keep the machine awake for long exports (best effort).
        releaseWakeLock = acquireWakeLock();

        let hasReachedEnd = false;
        let lastProgress = 10;
        let lastReportedTime = trimStart;
        let lastAdvanceAt = Date.now();

        const finish = (reason: string) => {
          if (hasReachedEnd) return;
          hasReachedEnd = true;

          if (stopTicker) {
            stopTicker();
            stopTicker = null;
          }

          console.log(`Export finishing (${reason}) at:`, videoEl.currentTime, 'trimEnd:', trimEnd);

          // Draw the final frame, then stop feeding both tracks at the same
          // moment so picture and sound end together.
          drawComposedFrame(videoEl.currentTime);
          videoEl.pause();

          // Small grace period so the encoder can flush the last frame.
          // Scheduled off the worker clock rather than setTimeout: a hidden tab
          // clamps main-thread timers to a second or more, and stretching this
          // 50 ms wait into a minute would append exactly the held-frame-with-
          // running-audio tail this rewrite exists to remove.
          scheduleBackgroundTimeout(50, () => {
            if (recorder && recorder.state === 'recording') {
              recorder.stop();
            }
          });
        };

        // A decode stall that never recovers would otherwise hang the export
        // forever. If the element's clock stops advancing while it is meant to
        // be playing, wrap up with what we have rather than spinning.
        const STALL_TIMEOUT_MS = 15000;

        const tick = () => {
          if (abortController.signal.aborted || hasReachedEnd || exportComplete) return;

          const currentTime = videoEl.currentTime;

          if (currentTime > lastReportedTime + 0.001) {
            lastReportedTime = currentTime;
            lastAdvanceAt = Date.now();
          }

          // Stop when we reach the trim end or the video runs out.
          if (videoEl.ended || currentTime >= trimEnd - 0.02) {
            finish(videoEl.ended ? 'video ended' : 'reached trim end');
            return;
          }

          if (Date.now() - lastAdvanceAt > STALL_TIMEOUT_MS) {
            finish('playback stalled');
            return;
          }

          drawComposedFrame(currentTime);

          // Update progress based on trimmed duration
          if (trimmedDuration > 0) {
            const elapsed = currentTime - trimStart;
            const progress = 10 + Math.min((elapsed / trimmedDuration) * 85, 85);
            if (progress - lastProgress > 1) {
              lastProgress = progress;
              onProgress?.(Math.round(progress), 'rendering');
            }
          }
        };

        // Second, independent stop trigger: `ended` fires from the media
        // element itself, so an untrimmed export still terminates cleanly even
        // if the ticker is delayed.
        videoEl.addEventListener('ended', () => finish('ended event'));

        // Start playback from the trim start. The element is unmuted so its
        // audio reaches the capture graph; if the browser refuses to autoplay
        // audible media anyway, fall back to a silent (picture-only) export
        // rather than failing outright.
        try {
          await videoEl.play();
        } catch (playError) {
          console.warn('Audible playback blocked — exporting without audio:', playError);
          videoEl.muted = true;
          await videoEl.play();
        }

        // Drive the render loop from a worker timer — unlike rAF, it keeps
        // firing when the tab is not in front.
        stopTicker = createBackgroundInterval(FRAME_INTERVAL_MS, tick);
      } catch (error) {
        fail(error instanceof Error ? error : new Error(String(error)));
      }
    })();
  });
};

/**
 * Download video file
 */
export const downloadVideo = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Save video with file picker (if supported)
 */
export const saveVideoWithPicker = async (blob: Blob, suggestedName: string, extension: string): Promise<string | null> => {
  // Determine file type based on extension
  const fileTypes = extension === '.mp4' 
    ? [{ description: 'MP4 Video', accept: { 'video/mp4': ['.mp4'] } }]
    : [{ description: 'WebM Video', accept: { 'video/webm': ['.webm'] } }];
  
  // Use File System Access API if available
  if ('showSaveFilePicker' in window) {
    try {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName,
        types: fileTypes,
      });
      
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      
      return handle.name;
    } catch (err: any) {
      if (err.name === 'AbortError') return null;
      // Fall through to download
    }
  }
  
  // Fallback to download
  downloadVideo(blob, suggestedName);
  return suggestedName;
};
